import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangle, ArrowRight, GraduationCap, ShieldCheck, Sparkles } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { GuideDiscussion } from "@/components/resources/GuideDiscussion";
import { KnowledgeGrid } from "@/components/resources/KnowledgeGrid";
import { FRESHER_GROUPS, REFUSAL_GROUPS, ALL_FAQS } from "@/data/fresher-guide";


const TITLE = "F-1 Visa Knowledge Hub & Checklist | WeLivdIt";
const DESCRIPTION =
  "Essential F-1 rules, SEVIS & DS-160 timelines, funding logic, interview psychology, and 214(b) recovery strategies - from the WeLivdIt Specialists.";

export const Route = createFileRoute("/resources/f1-visa-guide")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://welivdit.com/resources/f1-visa-guide" },
      { property: "og:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: "https://welivdit.com/og-share-v6.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://welivdit.com/resources/f1-visa-guide" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "F-1 Visa Knowledge Hub & Checklist",
          description: DESCRIPTION,
          image: "https://welivdit.com/og-share-v6.jpg",
          mainEntityOfPage: "https://welivdit.com/resources/f1-visa-guide",
          author: { "@type": "Organization", name: "WeLivdIt" },
          publisher: {
            "@type": "Organization",
            name: "WeLivdIt",
            url: "https://welivdit.com",
            logo: { "@type": "ImageObject", url: "https://welivdit.com/og-share-v6.jpg" },
          },
          about: ["F-1 student visa", "US visa interview", "214(b) refusal", "DS-160", "SEVIS fee"],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://welivdit.com" },
            { "@type": "ListItem", position: 2, name: "Resources", item: "https://welivdit.com/resources" },
            {
              "@type": "ListItem",
              position: 3,
              name: "F-1 Visa Knowledge Hub & Checklist",
              item: "https://welivdit.com/resources/f1-visa-guide",
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: ALL_FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.plain },
          })),
        }),
      },
    ],
  }),
  component: FresherGuidePage,
});

type TabKey = "all" | "fresher" | "refusal";

const TABS: { key: TabKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "fresher", label: "Fresher Guide" },
  { key: "refusal", label: "Refusal (214b) Guide" },
];

const JUMP_LINKS = [
  { id: "checklist", label: "2026 Knowledge Hub" },
  { id: "fresher", label: "Fresher Guide" },
  { id: "refusal", label: "Refusal (214b)" },
  { id: "strategy", label: "Strategy Table" },
  { id: "community", label: "Community Queries" },
] as const;


const STRATEGY_ROWS = [
  {
    topic: "Low GPA / Backlogs",
    weak: '"My GPA was low because college was difficult."',
    strong:
      '"My academics had initial setbacks, but in my final years I focused deeply on my specialization and built clear long-term career goals."',
  },
  {
    topic: "Sponsorship",
    weak: "Dad + Mom + Grandfather + Aunt + loan, with no clear primary source.",
    strong:
      '"My education is primarily funded through an approved education loan, supported by family savings from my parents."',
  },
  {
    topic: "Return Intent",
    weak: '"I want global exposure and then I will return to India."',
    strong:
      '"I am specializing in Supply Chain Analytics to return and help solve the 15% operational loss in our family enterprise / target industry in India."',
  },
  {
    topic: "DS-160 Updates",
    weak: "Editing details last minute or contradicting the form during the interview.",
    strong:
      "New DS-160 submitted 2+ days before biometrics, both confirmation pages carried, spoken answers matching every field.",
  },
];

function FresherGuidePage() {
  const [tab, setTab] = useState<TabKey>("all");
  const showFresher = tab === "all" || tab === "fresher";
  const showRefusal = tab === "all" || tab === "refusal";




  return (
    <article className="bg-background">
      {/* Hero */}
      <section className="bg-gradient-hero">
        <div className="mx-auto max-w-4xl px-5 pt-14 pb-10 md:pt-20">
          <div className="rounded-3xl border border-white/25 bg-white/50 p-6 shadow-soft backdrop-blur-xl md:p-9 dark:border-white/10 dark:bg-white/[0.04]">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-accent/40 px-3 py-1.5 text-xs uppercase tracking-wider text-foreground/70">
              <GraduationCap className="h-3.5 w-3.5 text-primary" /> WeLivdIt Guide
            </div>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-balance leading-[1.1]">
              🎓 F-1 Visa Knowledge Hub &amp; Checklist
            </h1>
            <p className="mt-4 text-base md:text-lg text-foreground/70 leading-relaxed">
              Essential Rules, Interview Psychology, &amp; 214(b) Recovery Strategies
            </p>

          </div>
        </div>
      </section>

      {/* Sticky quick-jump pills */}
      <nav className="sticky top-16 z-30 border-y border-border/60 bg-background/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-4xl gap-2 overflow-x-auto px-5 py-2.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {JUMP_LINKS.map((j) => (
            <a
              key={j.id}
              href={`#${j.id}`}
              className="whitespace-nowrap rounded-full border border-border bg-card px-3.5 py-1.5 text-xs font-semibold text-foreground/70 transition hover:border-primary/60 hover:text-primary"
            >
              {j.label}
            </a>
          ))}
        </div>
      </nav>

      <div className="mx-auto max-w-4xl px-5">
        {/* 9-card knowledge grid */}
        <KnowledgeGrid />


        {/* Red alert */}
        <div className="mt-6 rounded-3xl border-2 border-red-500/70 bg-red-50 p-6 md:p-7 dark:bg-red-950/25">
          <h2 className="flex items-center gap-2 font-display text-lg md:text-xl font-bold text-red-800 dark:text-red-200">
            <AlertTriangle className="h-5 w-5" /> ⚠️ State Department Screening Directive
          </h2>
          <p className="mt-3 text-sm md:text-base leading-relaxed text-red-950/90 dark:text-red-100/85">
            Consular officers strictly screen non-immigrant intent. Answering &quot;Yes&quot; to experiencing or fearing
            harm in your home country triggers an immediate 214(b) non-immigrant visa denial. Keep your focus 100% on
            education, career growth, and your return to India.
          </p>
        </div>

        {/* Filter tabs */}
        <div className="mt-10 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={
                "rounded-full border px-4 py-2 text-sm font-semibold transition " +
                (tab === t.key
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-card text-foreground/70 hover:border-primary/50")
              }
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Fresher section */}
        {showFresher && (
          <section id="fresher" className="mt-10 scroll-mt-32">
            <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">
              Section 1 · F-1 Fresher Guide
            </h2>
            {FRESHER_GROUPS.map((group) => (
              <div key={group.category} className="mt-7">
                <h3 className="text-sm font-bold uppercase tracking-wider text-primary">{group.category}</h3>
                <Accordion type="single" collapsible className="mt-3">
                  {group.items.map((item) => (
                    <AccordionItem
                      key={item.q}
                      value={item.q}
                      className="rounded-2xl border border-border bg-card mb-3 px-4 transition hover:border-primary/50 hover:shadow-soft"
                    >
                      <AccordionTrigger className="text-left font-semibold">{item.q}</AccordionTrigger>
                      <AccordionContent className="text-sm leading-relaxed text-foreground/80">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </section>
        )}

        {/* Refusal section */}
        {showRefusal && (
          <section id="refusal" className="mt-12 scroll-mt-32">
            <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">
              Section 2 · F-1 Refusal Guide (214b)
            </h2>
            {REFUSAL_GROUPS.map((group) => (
              <div key={group.category} className="mt-7">
                <h3 className="text-sm font-bold uppercase tracking-wider text-destructive">{group.category}</h3>
                <Accordion type="single" collapsible className="mt-3">
                  {group.items.map((item) => (
                    <AccordionItem
                      key={item.q}
                      value={item.q}
                      className="rounded-2xl border border-border bg-card mb-3 px-4 transition hover:border-primary/50 hover:shadow-soft"
                    >
                      <AccordionTrigger className="text-left font-semibold">{item.q}</AccordionTrigger>
                      <AccordionContent className="text-sm leading-relaxed text-foreground/80">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </section>
        )}


        {/* Strategy table */}
        <section id="strategy" className="mt-14 scroll-mt-32">
          <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">
            📌 Quick Strategy Comparison Table
          </h2>
          <div className="mt-5 overflow-x-auto rounded-2xl border border-border">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead className="bg-muted/60 text-xs uppercase tracking-wider text-foreground/60">
                <tr>
                  <th className="px-4 py-3 font-semibold">Topic</th>
                  <th className="px-4 py-3 font-semibold">❌ Weak approach</th>
                  <th className="px-4 py-3 font-semibold">✅ Strong approach</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {STRATEGY_ROWS.map((r) => (
                  <tr key={r.topic} className="align-top">
                    <td className="px-4 py-4 font-semibold">{r.topic}</td>
                    <td className="px-4 py-4 text-foreground/70">{r.weak}</td>
                    <td className="px-4 py-4 text-foreground/85">{r.strong}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* CTA */}
        <section className="mt-14">
          <div className="rounded-3xl border border-primary/30 bg-primary/[0.06] p-6 md:p-7">
            <p className="flex items-start gap-2.5 text-sm md:text-base font-semibold leading-relaxed">
              <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <span>
                💡 WE LIVED IT RULE OF THUMB: Preparation builds confidence, but AUTHENTICITY builds trust. Visa
                interviews are credibility tests, not public speaking exams.
              </span>
            </p>
          </div>

          <div className="mt-6 rounded-3xl border border-border bg-card p-6 md:p-8">
            <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
              <ShieldCheck className="h-4 w-4" /> The WeLivdIt Method
            </p>
            <h3 className="mt-2 font-display text-xl md:text-2xl font-semibold">
              Want us to review your case before your interview?
            </h3>
            <p className="mt-2 text-sm text-foreground/70">
              A Specialist audits your DS-160, funding logic, and story - then runs live mocks until your answers hold
              up under pressure.
            </p>
            <Link
              to="/pricing"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-gradient-cta px-6 py-3 font-semibold text-primary-foreground transition hover:opacity-90"
            >
              Book Mock Review <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>

        {/* Community */}
        <div className="pb-20">
          <GuideDiscussion pageSlug="fresher-guide" />
        </div>
      </div>
    </article>
  );
}
